import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Award, ArrowRight, ShieldCheck, Clock, Layers } from 'lucide-react';
import { QAVerifiedBadge } from './ProgrammeBadges';
import { CredentialLevelBadge } from './ProgrammeBadges';
import { trackProgrammeCardClick } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Data-driven Specialist Programmes rail.
 *
 * Auto-scaling guardrail: this pulls the LIVE public catalogue
 * (`/api/programmes/public/catalogue`), so any newly published programme
 * surfaces here with zero code/content changes. Renders NOTHING at 0-state
 * (or while only siblings exist after exclusion) so it hides gracefully while
 * the catalogue is small.
 *
 * Reused on: homepage rail (P0-2), SEO comparison/pillar pages (P0-3),
 * and the programme detail "more credentials" rail.
 */
export default function ProgrammesRail({
  pillar = '',
  excludeId = null,
  limit = 3,
  eyebrow = 'Specialist Credentials',
  title = 'Earn the credential — Specialist Programmes',
  subtitle = 'Credentialed, multi-course pathways for practitioners and auditors — quality-assured and human-reviewed.',
  source = 'rail',
  className = 'py-12',
}) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const params = {};
    if (pillar) params.pillar = pillar;
    axios.get(`${API_URL}/api/programmes/public/catalogue`, { params })
      .then(res => {
        let progs = res.data.programmes || [];
        if (excludeId) progs = progs.filter(p => p.id !== excludeId);
        setItems(progs.slice(0, limit));
      })
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, [pillar, excludeId, limit]);

  // 0-state safety — hide entirely until we have at least one programme to show.
  if (!loaded || items.length === 0) return null;

  const open = (p) => {
    trackProgrammeCardClick(p, source);
    navigate(`/library/programme/${p.slug}`);
  };

  return (
    <section className={className} data-testid="programmes-rail">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-600 dark:text-violet-300">
              <Award className="w-3.5 h-3.5" /> {eyebrow}
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight mt-1" data-testid="programmes-rail-heading">
              {title}
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 max-w-2xl">{subtitle}</p>
          </div>
          <button
            onClick={() => navigate('/library/programmes')}
            data-testid="programmes-rail-browse-all"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 whitespace-nowrap"
          >
            Browse all <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <button
              key={p.id}
              onClick={() => open(p)}
              data-testid={`rail-programme-card-${p.id}`}
              className="group text-left bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-lg transition-all flex flex-col"
            >
              <div className="relative">
                {p.cover_image_url ? (
                  <img src={p.cover_image_url} alt="" loading="lazy" className="w-full aspect-[3/2] object-cover" />
                ) : (
                  <div className="w-full aspect-[3/2] bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                    <GraduationCap className="w-9 h-9 text-white/80" />
                  </div>
                )}
                {p.featured && (
                  <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-amber-950 px-2 py-0.5 rounded-full shadow">★ Featured</span>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-extrabold text-slate-900 dark:text-white text-lg leading-snug tracking-tight group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">{p.title}</h3>
                {p.competency_display && <p className="text-xs text-indigo-500 dark:text-indigo-400 font-semibold mt-1">{p.competency_display}</p>}
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-400 dark:text-slate-500">
                  <span className="flex items-center gap-1"><Layers className="w-3.5 h-3.5" /> {p.course_count} courses</span>
                  {p.duration?.days > 0 && (
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.duration.days_label}</span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2 mt-auto pt-4">
                  <CredentialLevelBadge level={p.credential_level} />
                  {p.qa_verified ? <QAVerifiedBadge verified /> : <ArrowRight className="w-4 h-4 text-violet-500 group-hover:translate-x-0.5 transition-transform" />}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="sm:hidden mt-5 text-center">
          <button
            onClick={() => navigate('/library/programmes')}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 dark:text-violet-400"
          >
            Browse all programmes <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
